/**
 * ============================================================================
 * KbDeleteCollectionDialog — Confirma exclusão de coleção
 * ----------------------------------------------------------------------------
 * Se a coleção tem páginas, o usuário escolhe entre "Deixar sem coleção" ou
 * "Mover páginas para outra coleção" (select das demais do mesmo space).
 * Subcoleções são excluídas em CASCADE (avisa o usuário via checkbox).
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useMemo, useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle } from "lucide-react";
import { useDeleteKbCollection, type KbCollectionRow } from "@/hooks/kb/useKb";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  spaceId: string;
  collection: KbCollectionRow;
  allCollections: KbCollectionRow[];
}

export function KbDeleteCollectionDialog({ open, onOpenChange, spaceId, collection, allCollections }: Props) {
  const del = useDeleteKbCollection(spaceId);

  // Descendentes (usadas para excluir da lista de destinos possíveis)
  const descendantIds = useMemo(() => {
    const set = new Set<string>([collection.id]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const c of allCollections) {
        if (c.parent_id && set.has(c.parent_id) && !set.has(c.id)) {
          set.add(c.id); changed = true;
        }
      }
    }
    return set;
  }, [allCollections, collection.id]);

  const hasChildren = allCollections.some((c) => c.parent_id === collection.id);
  const hasPages = collection.page_count > 0;

  const [mode, setMode] = useState<"orphan" | "reassign">("orphan");
  const [targetId, setTargetId] = useState<string | null>(null);
  const [confirmCascade, setConfirmCascade] = useState(false);

  useEffect(() => {
    if (open) {
      setMode("orphan");
      setTargetId(null);
      setConfirmCascade(false);
    }
  }, [open]);

  const validTargets = allCollections.filter((c) => !descendantIds.has(c.id));

  const canSubmit =
    !del.isPending &&
    (!hasChildren || confirmCascade) &&
    (!hasPages || mode === "orphan" || (mode === "reassign" && targetId));

  const handleDelete = async () => {
    if (!canSubmit) return;
    await del.mutateAsync({
      collection_id: collection.id,
      reassign_to_collection_id: hasPages && mode === "reassign" ? targetId : null,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Excluir coleção "{collection.name}"
          </DialogTitle>
          <DialogDescription>
            Esta ação não pode ser desfeita.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {hasPages && (
            <div>
              <Label className="mb-2 block">
                Esta coleção tem {collection.page_count} página{collection.page_count === 1 ? "" : "s"}.
                O que fazer com {collection.page_count === 1 ? "ela" : "elas"}?
              </Label>
              <RadioGroup value={mode} onValueChange={(v) => setMode(v as any)}>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="orphan" id="orphan" />
                  <Label htmlFor="orphan" className="font-normal cursor-pointer">
                    Deixar sem coleção (páginas ficam na raiz do space)
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="reassign" id="reassign" />
                  <Label htmlFor="reassign" className="font-normal cursor-pointer">
                    Mover para outra coleção
                  </Label>
                </div>
              </RadioGroup>
              {mode === "reassign" && (
                <div className="mt-2">
                  <Select value={targetId ?? ""} onValueChange={setTargetId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Escolha a coleção destino" />
                    </SelectTrigger>
                    <SelectContent>
                      {validTargets.length === 0 && (
                        <div className="p-2 text-xs text-muted-foreground">
                          Nenhuma outra coleção disponível.
                        </div>
                      )}
                      {validTargets.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.icon ?? "📁"} {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {hasChildren && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Todas as subcoleções serão excluídas em cascata.</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Páginas dessas subcoleções também ficarão sem coleção
                    {mode === "reassign" ? " (a movimentação acima só afeta as páginas diretas desta coleção)" : ""}.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Checkbox id="cascade" checked={confirmCascade} onCheckedChange={(v) => setConfirmCascade(!!v)} />
                <Label htmlFor="cascade" className="font-normal text-sm cursor-pointer">
                  Entendi e quero continuar
                </Label>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button variant="destructive" onClick={handleDelete} disabled={!canSubmit}>
            {del.isPending ? "Excluindo..." : "Excluir coleção"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
